import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { persistentSessionManager } from '../../shared/services/persistentSession';
import { sendNHBLog } from '../../shared/services/logging';

// API base URL - use relative path in production, localhost in development
const API_BASE_URL =
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  museum_id: number | null;
  museum_name?: string | null;
  museum_ids?: number[];
  museum_names?: string[];
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface CreateUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  password: string;
}

export interface UpdateUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
}

// Initial state - use persistent session manager
const getInitialState = (): AuthState => {
  const sessionInfo = persistentSessionManager.getSessionInfo();
  return {
    user: sessionInfo.user as User | null,
    token: sessionInfo.token,
    refreshToken: sessionInfo.hasRefreshToken ? persistentSessionManager.getStoredRefreshToken() : null,
    isAuthenticated: sessionInfo.isLoggedIn,
    isLoading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

// Async thunks
export const loginUser = createAsyncThunk('auth/login', async (credentials: LoginCredentials, { rejectWithValue }) => {
  try {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/token/`, credentials);

    // Use persistent session manager to store login data
    persistentSessionManager.login(response.data.access, response.data.user, response.data.refresh);

    const museum = (response.data.user as { museum_code?: string }).museum_code || 'NIL';
    try {
      await sendNHBLog({
        role: 'dashboard_user',
        robot: 'NIL',
        museum,
        task: 'dashboard_user_action',
        log: {
          action: 'login',
          user_id: response.data.user.id,
          username: response.data.user.username,
          user_role: response.data.user.role,
        },
      });
    } catch (logError) {
      // Logging should not block login
      console.warn('Failed to log login action:', logError);
    }

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { detail?: string } } };
    return rejectWithValue(axiosError.response?.data?.detail || 'Login failed');
  }
});

export const initializeAuth = createAsyncThunk('auth/initialize', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { auth: AuthState };
    const token = state.auth.token;

    if (!token) {
      throw new Error('No token available');
    }

    const response = await axios.get<User>(`${API_BASE_URL}/auth/profile/`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number } };
    // If token is invalid, clear it
    if (axiosError.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
    return rejectWithValue('Failed to initialize auth');
  }
});

export const refreshToken = createAsyncThunk('auth/refresh', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { auth: AuthState };
    const refreshToken = state.auth.refreshToken;

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post<{ access: string }>(`${API_BASE_URL}/auth/token/refresh/`, {
      refresh: refreshToken,
    });

    return response.data.access;
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { detail?: string } } };
    return rejectWithValue(axiosError.response?.data?.detail || 'Token refresh failed');
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async (_, { getState }) => {
  const state = getState() as { auth: AuthState };
  const user = state.auth.user;
  const museum = (user as { museum_code?: string } | null)?.museum_code || 'NIL';
  try {
    await sendNHBLog({
      role: 'dashboard_user',
      robot: 'NIL',
      museum,
      task: 'dashboard_user_action',
      log: {
        action: 'logout',
        user_id: user?.id,
        username: user?.username,
        user_role: user?.role,
      },
    });
  } catch (logError) {
    // Logging should not block logout
    console.warn('Failed to log logout action:', logError);
  }

  try {
    const token = state.auth.token;

    if (token) {
      await axios.post(
        `${API_BASE_URL}/auth/logout/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    }
  } catch (error) {
    // Ignore logout errors
  }

  // Use persistent session manager to clear session
  persistentSessionManager.logout();
});

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ token: string; refreshToken: string; user: User }>) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;
      state.isAuthenticated = true;

      // Use persistent session manager to store credentials
      persistentSessionManager.login(action.payload.token, action.payload.user, action.payload.refreshToken);
    },
    clearCredentials: state => {
      state.token = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;

      // Use persistent session manager to clear credentials
      persistentSessionManager.logout();
    },
  },
  extraReducers: builder => {
    builder
      // Initialize auth
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(initializeAuth.rejected, state => {
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        // Don't clear persistent session on initialization failure
      })

      // Login
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.error = null;
        // Persistent session manager already handled storage
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })

      // Refresh token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload;
        // Persistent session manager handles token storage
      })
      .addCase(refreshToken.rejected, state => {
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        // Don't clear persistent session on refresh failure - let user decide
      })

      // Logout
      .addCase(logoutUser.fulfilled, state => {
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { clearError, setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
